/**
 * RATE MODE TOGGLE
 * 
 * Componente para alternar entre taxas reais e nominais
 * Integrado com Fisher Calculator para conversão automática
 * 
 * Casos de Uso:
 * - Premissas de crescimento perpétuo (g)
 * - Taxas de desconto ajustadas
 * - Qualquer taxa que precise conversão real ↔ nominal
 * 
 * @module components/ui/RateModeToggle
 */

import React from 'react';
import {
  ToggleButton,
  ToggleButtonGroup,
  Box,
  Typography,
  Tooltip,
} from '@mui/material';
import { Info } from '@mui/icons-material';
import type { RateMode } from '../../types/valuation';

// ============================================================================
// INTERFACES
// ============================================================================

export interface RateModeToggleProps {
  /** Modo atual selecionado */
  value: RateMode;
  
  /** Callback quando modo muda */
  onChange: (mode: RateMode) => void;
  
  /** Label do componente (opcional) */
  label?: string;
  
  /** Tooltip explicativo (opcional) */
  tooltip?: string;
  
  /** Desabilitar toggle (opcional) */
  disabled?: boolean;
  
  /** Tamanho do toggle */
  size?: 'small' | 'medium' | 'large';
  
  /** Modo full-width */
  fullWidth?: boolean;
}

// ============================================================================
// COMPONENTE
// ============================================================================

/**
 * Toggle para selecionar modo de taxa (Real vs Nominal)
 * 
 * **Real:**
 * - Ajustado pela inflação
 * - Representa crescimento "real" da economia
 * - Exemplo: 3% real = 3% acima da inflação
 * 
 * **Nominal:**
 * - Inclui inflação
 * - Expectativas de mercado
 * - Exemplo: 7.635% nominal = 3% real + 4.5% inflação
 * 
 * @example
 * ```tsx
 * // Uso básico
 * <RateModeToggle
 *   value={gMode}
 *   onChange={setGMode}
 *   label="Crescimento Perpétuo (g)"
 * />
 * 
 * // Com tooltip customizado
 * <RateModeToggle
 *   value={gMode}
 *   onChange={setGMode}
 *   label="Taxa de Crescimento"
 *   tooltip="Escolha entre taxa real (ajustada) ou nominal (inclui inflação)"
 *   size="small"
 * />
 * ```
 */
export const RateModeToggle: React.FC<RateModeToggleProps> = ({
  value,
  onChange,
  label,
  tooltip = 'Real: ajustado pela inflação | Nominal: inclui inflação',
  disabled = false,
  size = 'small',
  fullWidth = false,
}) => {
  const handleChange = (
    _event: React.MouseEvent<HTMLElement>,
    newMode: RateMode | null,
  ) => {
    // Sempre manter um valor selecionado (não permite deselecionar)
    if (newMode !== null) {
      onChange(newMode);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        width: fullWidth ? '100%' : 'auto',
      }}
    >
      {/* Label com tooltip */}
      {label && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            {label}
          </Typography>
          <Tooltip
            title={tooltip}
            arrow
            placement="top"
            sx={{
              '& .MuiTooltip-tooltip': {
                bgcolor: '#1E2329',
                color: '#F8F8F2',
                border: '1px solid #374151',
                maxWidth: 300,
              },
            }}
          >
            <Info
              sx={{
                fontSize: 14,
                color: 'text.secondary',
                cursor: 'help',
              }}
            />
          </Tooltip>
        </Box>
      )}

      {/* Toggle buttons */}
      <ToggleButtonGroup
        value={value}
        exclusive
        onChange={handleChange}
        disabled={disabled}
        size={size}
        fullWidth={fullWidth}
        sx={{
          '& .MuiToggleButton-root': {
            textTransform: 'none',
            fontWeight: 500,
            px: 2,
            py: 0.75,
            border: '1px solid',
            borderColor: 'divider',
            
            '&.Mui-selected': {
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              borderColor: 'primary.main',
              
              '&:hover': {
                bgcolor: 'primary.dark',
              },
            },
            
            '&:not(.Mui-selected)': {
              bgcolor: 'background.paper',
              color: 'text.secondary',
              
              '&:hover': {
                bgcolor: 'action.hover',
              },
            },
          },
        }}
      >
        <ToggleButton value="real" aria-label="Taxa Real">
          Real
        </ToggleButton>
        <ToggleButton value="nominal" aria-label="Taxa Nominal">
          Nominal
        </ToggleButton>
      </ToggleButtonGroup>

      {/* Helper text com explicação do modo selecionado */}
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          fontSize: '0.7rem',
          fontStyle: 'italic',
        }}
      >
        {value === 'real' 
          ? 'Ajustado pela inflação (crescimento real da economia)'
          : 'Inclui inflação (expectativas nominais de mercado)'}
      </Typography>
    </Box>
  );
};

export default RateModeToggle;
